import type { AIProvider } from './types'
import { AiError } from './types'
import { OpenAIProvider } from './providers/openai'
import { AnthropicProvider } from './providers/anthropic'
import { OllamaProvider } from './providers/ollama'
import { ProxyProvider } from './proxy'
import type { AiSettings, ProviderId } from '@/types/settings'
import { useAuthStore } from '@/sync/auth'

/**
 * Construye el provider de IA adecuado según la configuración del
 * usuario. Es el único punto donde se decide qué adapter se usa.
 *
 *   routing 'direct'  → adapter concreto (OpenAI/Anthropic/Ollama).
 *   routing 'proxy'   → ProxyProvider que delega en el endpoint serverless.
 *
 * SEGURIDAD (multitenant): la IA solo está disponible para usuarios con
 * sesión iniciada. Sin sesión, se lanza antes de construir nada. Este es
 * el guard central que cubre todos los puntos de entrada (useAi, repaso
 * SRS, resumen cruzado), no solo la ocultación de la UI en Ajustes.
 */
export function createProvider(settings: AiSettings): AIProvider {
  // La app es multitenant: sin cuenta no se puede usar ni configurar la IA.
  // useAuthStore() se llama aquí (no a nivel módulo) porque Pinia ya está
  // montado cuando se invoca IA; instanciarlo antes rompería en tests/SSR.
  const auth = useAuthStore()
  if (!auth.isAuthenticated) {
    throw new AiError('Inicia sesión para usar la IA')
  }

  const providerId: ProviderId = settings.activeProvider
  const cfg = settings.providers[providerId]

  if (!cfg) {
    throw new AiError(`Proveedor "${providerId}" no configurado.`)
  }

  // Modo proxy: la key vive en el servidor, elegimos proveedor por id.
  if (settings.routing === 'proxy') {
    if (!settings.proxyEndpoint) {
      throw new AiError('Modo proxy activo pero sin endpoint. Configúralo en Ajustes.')
    }
    return new ProxyProvider(settings.proxyEndpoint, providerId, cfg.model, settings.proxyToken)
  }

  // Modo directo: adapter concreto del proveedor.
  switch (providerId) {
    case 'openai':
      return new OpenAIProvider(cfg)
    case 'anthropic':
      return new AnthropicProvider(cfg)
    case 'ollama':
      return new OllamaProvider(cfg)
    default:
      throw new AiError(`Proveedor no soportado: ${providerId}`)
  }
}
